import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  ApiEnvelope, LoginCheckResponse, LoginResponse, Role, User
} from '../models/user.model';

export const TOKEN_KEY = 'prc_token';
export const USER_KEY = 'prc_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private _user = signal<User | null>(this.loadUser());

  readonly user = this._user.asReadonly();
  readonly isAuthed = computed(() => !!this._user());
  readonly role = computed<Role | null>(() => this._user()?.role ?? null);

  private loadUser(): User | null {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) as User : null;
    } catch {
      return null;
    }
  }

  token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /** Step 1 — probe whether this email needs the first-login flow. */
  checkEmail(email: string): Observable<LoginCheckResponse> {
    console.log(email)
  
    return this.http
      .post<ApiEnvelope<LoginCheckResponse>>(`${environment.apiUrl}/auth/login`, { email })
      .pipe(map(r => r.data as LoginCheckResponse));
  }

  /** Step 2 — full password login. */
  login(email: string, password: string): Observable<User> {
    return this.http
      .post<ApiEnvelope<LoginResponse>>(`${environment.apiUrl}/auth/login`, { email, password })
      .pipe(
        map(r => r.data as LoginResponse),
        tap(res => {
          localStorage.setItem(TOKEN_KEY, res.token);
          localStorage.setItem(USER_KEY, JSON.stringify(res.user));
          this._user.set(res.user);
        }),
        map(res => res.user)
      );
  }

  setPassword(token: string, password: string): Observable<unknown> {
    return this.http.post<ApiEnvelope<unknown>>(
      `${environment.apiUrl}/auth/set-password`,
      { token, password }
    );
  }

  forgotPassword(email: string): Observable<unknown> {
    return this.http.post<ApiEnvelope<unknown>>(
      `${environment.apiUrl}/auth/forgot-password`,
      { email }
    );
  }

  logout(redirect = true): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this._user.set(null);
    if (redirect) this.router.navigate(['/login']);
  }

  /** Where should a user of this role land after sign-in? */
  routeForRole(role: Role | null): string {
    return role === 'employee' ? '/employee' : '/dashboard';
  }
}